import { useState, useEffect } from "react";
import { Globe, Loader2, Download, Plus, ArrowRight, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LeadTable from "@/components/LeadTable";
import { toast } from "sonner";

export default function DomainScraper() {
  const [domain, setDomain] = useState("");
  const [roles, setRoles] = useState("");
  const [count, setCount] = useState("10");
  const [campaignId, setCampaignId] = useState("none");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  const scrape = async () => {
    const d = domain.trim().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!d) { toast.error("Enter a company domain"); return; }
    setLoading(true);
    setResults([]);
    try {
      const r = await api.post("/leads/scrape-domain", {
        domain: d,
        count: parseInt(count),
        icp_roles: roles || undefined,
        campaign_id: campaignId !== "none" ? campaignId : undefined,
      });
      setResults(r.data);
      toast.success(`Found ${r.data.length} decision-makers`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scraping failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="label-mono mb-2">— Lead Generation</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Domain Scraper</h1>
        <p className="text-[#5C5C66] mt-2">Paste a company domain — our AI finds the key decision-makers and enriches their profiles.</p>
      </div>

      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="lg:col-span-2">
            <Label className="label-mono">Company domain</Label>
            <div className="relative mt-2">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9A9AA3]" />
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && scrape()}
                placeholder="stripe.com"
                className="pl-9 rounded-lg"
                data-testid="domain-input"
              />
            </div>
          </div>
          <div>
            <Label className="label-mono">Number of leads</Label>
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger className="mt-2 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["5", "10", "15", "20", "25"].map((n) => <SelectItem key={n} value={n}>{n} leads</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono">Save to campaign</Label>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger className="mt-2 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Don't save to campaign</SelectItem>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mb-4">
          <Label className="label-mono">Target roles (optional)</Label>
          <Input
            value={roles}
            onChange={(e) => setRoles(e.target.value)}
            placeholder="VP Sales, Marketing Director, Head of Growth"
            className="mt-2 rounded-lg"
          />
        </div>
        <Button onClick={scrape} disabled={loading} className="rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white shadow-soft" data-testid="scrape-btn">
          {loading
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scraping decision-makers…</>
            : <><Globe className="h-4 w-4 mr-2" /> Scrape domain <ArrowRight className="h-4 w-4 ml-2" /></>}
        </Button>
      </div>

      {loading && (
        <div className="bg-white border border-[#EAEAE3] rounded-2xl p-12 text-center shadow-soft">
          <Loader2 className="h-8 w-8 animate-spin text-[#002FA7] mx-auto mb-4" />
          <div className="font-display text-lg font-bold tracking-tighter">Researching {domain}…</div>
          <p className="text-[#5C5C66] text-sm mt-2">AI is finding decision-makers and enriching their profiles.</p>
        </div>
      )}

      {results.length > 0 && !loading && (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 flex items-start gap-4 shadow-sm animate-in fade-in slide-in-from-bottom-2">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <div className="font-bold text-amber-900 text-lg tracking-tight mb-1">Protect Your Email Sender Reputation</div>
              <div className="text-sm text-amber-800 leading-relaxed">
                LeadsB2B uses advanced AI to estimate key decision-makers and their structured emails. To avoid high bounce rates and domain blacklisting, <strong>always verify these emails</strong> using a tool like ZeroBounce or Hunter before triggering cold outreach sequences.
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EAEAE3] rounded-2xl shadow-soft overflow-hidden">
          <div className="p-5 border-b border-[#EAEAE3] flex items-center justify-between">
            <div>
              <div className="font-display text-lg font-bold tracking-tighter">{results.length} decision-makers found</div>
              <div className="text-xs text-[#5C5C66] mt-0.5">
                {campaignId !== "none" ? "Saved to campaign" : "Not saved to a campaign"}
              </div>
            </div>
          </div>
          <LeadTable leads={results} onRefresh={() => {}} />
        </div>
        </>
      )}

      {!loading && results.length === 0 && (
        <div className="bg-[#FAFAF7] border border-dashed border-[#EAEAE3] rounded-2xl p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border border-[#EAEAE3] flex items-center justify-center mx-auto mb-4 shadow-soft">
            <Globe className="h-7 w-7 text-[#9A9AA3]" />
          </div>
          <div className="font-display text-xl font-bold tracking-tighter mb-2">Enter a domain to get started</div>
          <p className="text-[#5C5C66] text-sm">Try <code className="bg-[#F4F4EE] px-1.5 py-0.5 rounded text-xs font-mono">openai.com</code> or <code className="bg-[#F4F4EE] px-1.5 py-0.5 rounded text-xs font-mono">stripe.com</code></p>
        </div>
      )}
    </div>
  );
}
